import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { getMigrationAuditReport, type AuditLocation } from "@/lib/migration-audit.functions";

export const Route = createFileRoute("/_authenticated/admin/migration-audit")({
  head: () => ({
    meta: [
      { title: "Migration Audit — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: MigrationAuditPage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-sm text-rose-600">Failed to load: {(error as Error).message}</div>
  ),
});

type Filter = "all" | "any_issue" | "missing_coords" | "missing_photos" | "empty_hours" | "missing_address";

function Stat({ label, value, tone, active, onClick }: {
  label: string; value: number; tone: "ok" | "warn" | "bad" | "muted"; active?: boolean; onClick?: () => void;
}) {
  const tones: Record<string, string> = {
    ok: "border-emerald-200 bg-emerald-50 text-emerald-800",
    warn: "border-amber-200 bg-amber-50 text-amber-800",
    bad: "border-rose-200 bg-rose-50 text-rose-800",
    muted: "border-slate-200 bg-slate-50 text-slate-700",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-xl border px-3 py-2.5 transition ${tones[tone]} ${active ? "ring-2 ring-navy/40" : "hover:opacity-90"}`}
    >
      <div className="text-[11px] font-semibold uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </button>
  );
}

function IssueBadge({ kind }: { kind: AuditLocation["issues"][number] }) {
  const map: Record<string, { label: string; cls: string }> = {
    missing_coords: { label: "No map pin", cls: "bg-rose-100 text-rose-800" },
    missing_photos: { label: "No photos", cls: "bg-amber-100 text-amber-800" },
    empty_hours: { label: "No hours", cls: "bg-amber-100 text-amber-800" },
    missing_address: { label: "No address", cls: "bg-rose-100 text-rose-800" },
  };
  const m = map[kind];
  return <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${m.cls}`}>{m.label}</span>;
}

function MigrationAuditPage() {
  const fetchReport = useServerFn(getMigrationAuditReport);
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["migration-audit"],
    queryFn: () => fetchReport(),
  });

  const [filter, setFilter] = useState<Filter>("any_issue");
  const [q, setQ] = useState("");

  const rows = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    return data.rows.filter((r) => {
      if (filter === "all") { /* ok */ }
      else if (filter === "any_issue") { if (r.issues.length === 0) return false; }
      else if (!r.issues.includes(filter)) return false;
      if (!term) return true;
      return (
        (r.place_name ?? "").toLowerCase().includes(term) ||
        (r.city_slug ?? "").toLowerCase().includes(term) ||
        (r.address ?? "").toLowerCase().includes(term) ||
        (r.place_slug ?? "").toLowerCase().includes(term)
      );
    });
  }, [data, filter, q]);

  const exportCsv = () => {
    if (!rows.length) return;
    const header = ["place_name","place_slug","section","label","city","address","is_primary","lat","lng","location_photos","place_photos","hours_days_set","issues"];
    const csv = [header.join(",")].concat(
      rows.map((r) => [
        r.place_name, r.place_slug, r.place_section, r.label, r.city_slug, r.address,
        r.is_primary, r.lat, r.lng, r.photo_count, r.place_photo_count, r.hours_days_set,
        r.issues.join("|"),
      ].map((v) => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(","))
    ).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `migration-audit-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:py-8">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-navy">Migration Audit</h1>
          <p className="mt-1 text-sm text-slate-600">Locations missing photos, coordinates, hours, or address after the multi-location import.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => refetch()} disabled={isFetching} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>
          <button onClick={exportCsv} disabled={!rows.length} className="rounded-lg bg-navy px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50">
            Export CSV
          </button>
        </div>
      </header>

      {isLoading && <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-600">Loading audit…</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
            <Stat label="Total" value={data.totals.locations} tone="muted" active={filter === "all"} onClick={() => setFilter("all")} />
            <Stat label="Clean" value={data.totals.clean} tone="ok" />
            <Stat label="Any Issue" value={data.totals.locations - data.totals.clean} tone="warn" active={filter === "any_issue"} onClick={() => setFilter("any_issue")} />
            <Stat label="No Map Pin" value={data.totals.missing_coords} tone="bad" active={filter === "missing_coords"} onClick={() => setFilter("missing_coords")} />
            <Stat label="No Photos" value={data.totals.missing_photos} tone="warn" active={filter === "missing_photos"} onClick={() => setFilter("missing_photos")} />
            <Stat label="No Hours" value={data.totals.empty_hours} tone="warn" active={filter === "empty_hours"} onClick={() => setFilter("empty_hours")} />
          </div>

          <div className="mb-3 flex flex-wrap gap-2 items-center">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, city, address…"
              className="flex-1 min-w-[200px] rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm"
            />
            <div className="text-xs text-slate-500 tabular-nums">{rows.length} shown</div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="px-3 py-2">Place</th>
                    <th className="px-3 py-2">Location</th>
                    <th className="px-3 py-2">City</th>
                    <th className="px-3 py-2 text-center">Coords</th>
                    <th className="px-3 py-2 text-center">Photos</th>
                    <th className="px-3 py-2 text-center">Hours</th>
                    <th className="px-3 py-2">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 align-top hover:bg-slate-50/50">
                      <td className="px-3 py-2">
                        {r.place_slug ? (
                          <Link to="/place/$id" params={{ id: r.place_slug }} className="font-medium text-navy underline-offset-2 hover:underline">
                            {r.place_name ?? r.place_slug}
                          </Link>
                        ) : (
                          <span className="font-medium">{r.place_name ?? "—"}</span>
                        )}
                        {r.place_section && <div className="text-[11px] text-slate-500">{r.place_section}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-slate-800">{r.label ?? "—"} {r.is_primary && <span className="ml-1 text-[10px] text-teal font-semibold">MAIN</span>}</div>
                        {r.address && <div className="text-[11px] text-slate-500">{r.address}</div>}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{r.city_slug ?? "—"}</td>
                      <td className="px-3 py-2 text-center tabular-nums">
                        {r.lat != null && r.lng != null
                          ? <span className="text-emerald-700">✓</span>
                          : <span className="text-rose-600">—</span>}
                      </td>
                      <td className="px-3 py-2 text-center tabular-nums text-xs">
                        <div>loc: <b>{r.photo_count}</b></div>
                        <div className="text-slate-500">place: {r.place_photo_count}</div>
                      </td>
                      <td className="px-3 py-2 text-center tabular-nums">{r.hours_days_set}/7</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {r.issues.length === 0
                            ? <span className="text-[10px] text-emerald-700 font-semibold">CLEAN</span>
                            : r.issues.map((i) => <IssueBadge key={i} kind={i} />)}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!rows.length && (
                    <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-500 text-sm">No locations match this filter.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
